import React, { useState, useEffect } from "react";
import * as microsoftTeams from "@microsoft/teams-js";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import { FluentProvider, webLightTheme, webDarkTheme } from "@fluentui/react-components";

import PrivacyTab from "../tabs/PrivacyTab";
import TermsOfUseTab from "../tabs/TermsOfUseTab";
import SettingsTab from "../tabs/SettingsTab";
import ReportsTab from "../tabs/ReportsTab";
import TimeEntryTab from "../tabs/TimeEntryTab";
import ContextProvider from "../providers/ContextProvider";
import { ApiProvider } from "../providers/ApiProvider";
import AlertProvider from "../providers/AlertProvider";

/**
 * The main app which handles the initialization and routing
 * of the app.
 */
/**
 * Component that syncs Fluent UI CSS variables to :root for global access.
 * Renders inside FluentProvider so it has access to Fluent UI CSS variables.
 * Uses a hidden element to read computed CSS variable values.
 */
function ThemeVariableSync() {
    const syncRef = React.useRef<HTMLDivElement>(null);

    useEffect(() => {
        if (!syncRef.current) return;

        const root = document.documentElement;

        // Read computed background color from element that uses the CSS variable
        const testEl = document.createElement("div");
        testEl.style.backgroundColor = "var(--colorNeutralBackground1)";
        testEl.style.position = "absolute";
        testEl.style.visibility = "hidden";
        testEl.style.pointerEvents = "none";

        // Append to sync element (inside FluentProvider, so CSS variables are available)
        syncRef.current.appendChild(testEl);

        const bg = window.getComputedStyle(testEl).backgroundColor;

        // Clean up
        syncRef.current.removeChild(testEl);

        // Set on :root if we got a valid color
        if (bg && bg !== "rgba(0, 0, 0, 0)") {
            root.style.setProperty("--theme-bg-1", bg);
        }
    });

    // Hidden element inside FluentProvider to read CSS variables from
    return <div ref={syncRef} className="css-variable-sync" aria-hidden="true" />;
}

function App() {
    const [theme, setTheme] = useState(webLightTheme);

    const applyTheme = (teamsTheme?: string) => {
        const root = document.documentElement;

        if (teamsTheme === "dark" || teamsTheme === "contrast") {
            setTheme(webDarkTheme);
            root.setAttribute("data-color-scheme", "dark");
        } else {
            setTheme(webLightTheme);
            root.setAttribute("data-color-scheme", "light");
        }
    };

    useEffect(() => {
        // Initialize the Microsoft Teams SDK
        microsoftTeams.app.initialize();

        // Get the Teams theme
        microsoftTeams.app.getContext().then((context) => {
            applyTheme(context.app?.theme);
        });

        // Listen for theme changes
        microsoftTeams.app.registerOnThemeChangeHandler((newTheme) => {
            applyTheme(newTheme);
        });
    }, []);

    // Display the app home page hosted in Teams
    return (
        <FluentProvider theme={theme}>
            <ThemeVariableSync />
            <AlertProvider>
                <ApiProvider>
                    <ContextProvider>
                        <BrowserRouter>
                            <Routes>
                                <Route path="/privacy" element={<PrivacyTab />} />
                                <Route path="/termsofuse" element={<TermsOfUseTab />} />
                                <Route path="/" element={<TimeEntryTab />} />
                                <Route path="/settings" element={<SettingsTab />} />
                                <Route path="/reports" element={<ReportsTab />} />
                            </Routes>
                        </BrowserRouter>
                    </ContextProvider>
                </ApiProvider>
            </AlertProvider>
        </FluentProvider>
    );
}

export default App;
